import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import ilustracionAnuncio from '../assets/anuncio-firma-guiada.png'
import { forceTour } from '../hooks/usePageTour'
import { prepararNovedadFirmaGuiada, usuarioEnSesionId } from '../config/newsTours'
import {
  anuncioPendientePara,
  instalarAyudaDesarrolloAnuncio,
  marcarAnuncioVisto,
} from '../config/releaseAnnouncements'

const RUTAS_PUBLICAS = ['/login', '/reset-password', '/registrar', '/firma-exitosa']
function rutaPublica(pathname) {
  if (pathname === '/') return true
  return RUTAS_PUBLICAS.some((ruta) => pathname === ruta || pathname.startsWith(`${ruta}/`))
}

export default function ReleaseAnnouncement() {
  const location = useLocation()
  const userId = usuarioEnSesionId()
  const anuncio = !rutaPublica(location.pathname) ? anuncioPendientePara(userId) : null
  const [abierto, setAbierto] = useState(false)
  const [restante, setRestante] = useState(15)
  const dialogoRef = useRef(null)
  const verRef = useRef(null)
  const entendidoRef = useRef(null)
  const cerradoRef = useRef(false)

  useEffect(() => {
    instalarAyudaDesarrolloAnuncio()
  }, [])

  useEffect(() => {
    if (!anuncio || cerradoRef.current) {
      setAbierto(false)
      return
    }
    setRestante(anuncio.bloqueoSegundos || 15)
    setAbierto(true)
  }, [anuncio])

  useEffect(() => {
    if (!abierto) {
      if (!window.__releaseAnnouncementClosing) window.__releaseAnnouncementActive = false
      return undefined
    }
    window.__releaseAnnouncementActive = true
    const root = document.getElementById('root')
    root?.setAttribute('inert', '')
    const previo = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    dialogoRef.current?.focus()
    const bloquearEscape = (event) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
    }
    window.addEventListener('keydown', bloquearEscape, true)
    return () => {
      window.removeEventListener('keydown', bloquearEscape, true)
      root?.removeAttribute('inert')
      document.body.style.overflow = previo
      if (!window.__releaseAnnouncementClosing) window.__releaseAnnouncementActive = false
    }
  }, [abierto])

  useEffect(() => {
    if (!abierto || restante <= 0) return undefined
    const timer = window.setTimeout(() => setRestante((valor) => valor - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [abierto, restante])

  useEffect(() => {
    if (abierto && restante === 0) verRef.current?.focus()
  }, [abierto, restante])

  if (!anuncio || !abierto) return null

  const bloqueado = restante > 0
  const total = anuncio.bloqueoSegundos || 15
  const avance = Math.min(100, ((total - restante) / total) * 100)

  const cerrar = (iniciarTour) => {
    if (bloqueado || cerradoRef.current) return
    cerradoRef.current = true
    marcarAnuncioVisto(anuncio, userId)
    window.__releaseAnnouncementClosing = true
    window.__releaseAnnouncementActive = false
    const root = document.getElementById('root')
    root?.removeAttribute('inert')
    document.body.style.overflow = ''
    setAbierto(false)
    window.dispatchEvent(new CustomEvent('app-release:closed', { detail: { tour: Boolean(iniciarTour) } }))
    if (iniciarTour) {
      prepararNovedadFirmaGuiada(userId)
      window.setTimeout(() => {
        window.__releaseAnnouncementClosing = false
        forceTour(anuncio.tourId)
      }, 0)
      return
    }
    window.__releaseAnnouncementClosing = false
  }

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    if (event.key !== 'Tab') return
    if (bloqueado) {
      event.preventDefault()
      dialogoRef.current?.focus()
      return
    }
    const focos = [verRef.current, entendidoRef.current].filter(Boolean)
    if (focos.length === 0) return
    const primero = focos[0]
    const ultimo = focos[focos.length - 1]
    if (event.shiftKey && document.activeElement === primero) {
      event.preventDefault()
      ultimo.focus()
    } else if (!event.shiftKey && document.activeElement === ultimo) {
      event.preventDefault()
      primero.focus()
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[130000] flex items-center justify-center bg-slate-950/80 p-4 sm:p-8"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) event.preventDefault()
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <div
        ref={dialogoRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="anuncio-actualizacion-titulo"
        aria-describedby="anuncio-actualizacion-subtitulo"
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="flex max-h-[calc(100vh-2rem)] w-full max-w-5xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl outline-none"
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50">
            <h1 id="anuncio-actualizacion-titulo" className="sr-only">{anuncio.titulo}</h1>
            <p id="anuncio-actualizacion-subtitulo" className="sr-only">{anuncio.subtitulo}</p>
            <ul className="sr-only">
              {anuncio.novedades.map((item) => (
                <li key={item.id}>{item.titulo}. {item.texto}</li>
              ))}
            </ul>
            <img
              src={ilustracionAnuncio}
              alt="PDFSigner evoluciona. Nuevas herramientas para preparar, revisar y firmar documentos con mayor precisión y trazabilidad."
              className="h-full w-full object-contain"
            />
          </div>
          <div className="border-t border-slate-200 bg-white px-6 py-5 sm:px-8">
              {bloqueado ? (
                <div aria-live="polite">
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-slate-800">Conoce las novedades</p>
                      <p className="mt-1 text-sm text-slate-500">Podrás continuar en {restante} segundos</p>
                    </div>
                    <p className="text-3xl font-semibold tabular-nums text-primary-800" aria-label={`${restante} segundos`}>
                      {restante} s
                    </p>
                  </div>
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-200" aria-hidden>
                    <div className="h-full rounded-full bg-primary-700 transition-all duration-1000 ease-linear" style={{ width: `${avance}%` }} />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <button
                    ref={entendidoRef}
                    type="button"
                    onClick={() => cerrar(false)}
                    aria-label="Entendido, continuar al aplicativo"
                    className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Entendido
                  </button>
                  <button
                    ref={verRef}
                    type="button"
                    onClick={() => cerrar(true)}
                    aria-label="Ver novedades de esta actualización"
                    className="rounded-xl bg-primary-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-800"
                  >
                    Ver novedades
                  </button>
                </div>
              )}
            </div>
          </div>
      </div>
    </div>,
    document.body
  )
}
